// src/components/OAuthWrapper.tsx - Updated to work with enhanced UserVerifiedRedirectSuccessHandler
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import axiosInstance from '@/lib/axiosInstance';
import { authSessionStorageService } from '@/services/sessionStorageService';
import { useAuth } from '@/lib/AuthContext';

interface OAuthWrapperProps {
  children: React.ReactNode;
}

export const OAuthWrapper: React.FC<OAuthWrapperProps> = ({ children }) => {
  const [isCheckingOAuth, setIsCheckingOAuth] = useState(true);
  const { refreshAuth } = useAuth();

  useEffect(() => {
    const checkOAuthCallback = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionIdFromUrl = urlParams.get('sessionId');
        const sessionPreserved = urlParams.get('sessionPreserved');
        const userParam = urlParams.get('user');
        
        console.log('🔍 OAuthWrapper: Checking for OAuth callback...', {
          hasSessionId: !!sessionIdFromUrl,
          sessionPreserved,
          user: userParam,
          currentPath: window.location.pathname,
          searchParams: window.location.search,
          referrer: document.referrer
        });

        // ENHANCED: Check for session preservation flow first
        if (sessionPreserved === 'true') {
          console.log('✅ OAuthWrapper: Session preservation flow detected for user:', userParam);
          
          try {
            // The backend preserved our original session with crypto keys and CSRF token
            const currentSessionData = authSessionStorageService.getAllSessionData();
            console.log('📋 OAuthWrapper: Current session data:', {
              hasSession: !!currentSessionData.sessionId,
              hasCSRF: !!currentSessionData.csrfToken,
              hasKeys: !!(currentSessionData.publicKey && currentSessionData.secretKey),
              hasLoginTime: !!currentSessionData.loginTime
            });

            // Clean up URL parameters first
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            // Verify the preserved session is working
            const response = await axiosInstance.get('/api/v1/auth/session');
            
            if (response.status === 200) {
              console.log('✅ OAuthWrapper: Preserved session verified with backend:', response.data.userName);
              
              // The original CSRF token should still be valid, but check if we need to update it
              const existingCsrfToken = authSessionStorageService.getCsrfToken();
              const headerCsrfToken = response.headers['x-csrf-token'];
              
              if (!existingCsrfToken && headerCsrfToken) {
                await authSessionStorageService.setCsrfToken(headerCsrfToken);
                console.log('✅ OAuthWrapper: CSRF token updated from preserved session');
              } else if (existingCsrfToken) {
                console.log('✅ OAuthWrapper: Original CSRF token preserved successfully');
              }
              
              // Update login time for the preserved session
              await authSessionStorageService.setLoginTime();
              
              // Refresh auth context
              console.log('🔄 OAuthWrapper: Triggering auth refresh for preserved session...');
              await refreshAuth();
              console.log('✅ OAuthWrapper: Session preservation completed successfully');
              
              // Set flags for compatibility
              localStorage.setItem('oauth_session_active', 'true');
              localStorage.setItem('oauth_user_data', JSON.stringify(response.data));
            } else {
              throw new Error(`Preserved session verification failed: ${response.status}`);
            }
          } catch (error: any) {
            console.error('❌ OAuthWrapper: Session preservation failed:', error.response?.status || error.message);
            // Clear session and let user login again
            authSessionStorageService.clearSession();
            localStorage.removeItem('oauth_session_active');
            localStorage.removeItem('oauth_user_data');
          }
        }
        // EXISTING: Traditional OAuth flow with session ID
        else if (sessionIdFromUrl) {
          console.log('✅ OAuthWrapper: Traditional OAuth session ID found in URL:', sessionIdFromUrl);
          
          try {
            // Store the session ID immediately
            await authSessionStorageService.setSession(sessionIdFromUrl);
            await authSessionStorageService.setLoginTime();
            
            console.log('✅ OAuthWrapper: Session stored successfully');
            
            // Clean up URL parameters
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
            
            // Verify the session works by checking /user endpoint
            const response = await axiosInstance.get('/api/v1/auth/user');
            
            if (response.status === 200) {
              console.log('✅ OAuthWrapper: Session verified with backend:', response.data.userName);
              
              // CRITICAL FIX: Get fresh CSRF token for the OAuth session
              console.log('🔑 OAuthWrapper: Fetching fresh CSRF token for OAuth session...');
              
              // The /user endpoint should return the CSRF token in headers
              const csrfToken = response.headers['x-csrf-token'];
              if (csrfToken) {
                await authSessionStorageService.setCsrfToken(csrfToken);
                console.log('✅ OAuthWrapper: CSRF token updated from /user response');
              } else {
                // Fallback: Make a request to get CSRF token
                console.log('🔄 OAuthWrapper: CSRF token not in /user response, fetching separately...');
                try {
                  const sessionResponse = await axiosInstance.get('/api/v1/auth/session');
                  const sessionCsrfToken = sessionResponse.headers['x-csrf-token'];
                  if (sessionCsrfToken) {
                    await authSessionStorageService.setCsrfToken(sessionCsrfToken);
                    console.log('✅ OAuthWrapper: CSRF token updated from /session response');
                  }
                } catch (csrfError) {
                  console.warn('⚠️ OAuthWrapper: Could not fetch CSRF token:', csrfError);
                }
              }
              
              // CRITICAL: Immediately refresh auth context
              console.log('🔄 OAuthWrapper: Triggering immediate auth refresh...');
              await refreshAuth();
              console.log('✅ OAuthWrapper: Auth context refreshed successfully');
              
              // Set a flag for any other components that might need it
              localStorage.setItem('oauth_session_active', 'true');
              localStorage.setItem('oauth_user_data', JSON.stringify(response.data));
            }
          } catch (error: any) {
            console.error('❌ OAuthWrapper: Failed to verify OAuth session:', error.response?.status || error.message);
            // Clear any stored session data if verification fails
            authSessionStorageService.clearSession();
            localStorage.removeItem('oauth_session_active');
            localStorage.removeItem('oauth_user_data');
          }
        } else {
          // EXISTING: Fallback OAuth indicators check
          const hasOAuthParams = urlParams.has('code') || urlParams.has('state');
          const fromMicrosoft = document.referrer.includes('login.microsoftonline.com');
          const isOnChatRoute = window.location.pathname === '/chat';
          
          if ((hasOAuthParams || fromMicrosoft) && isOnChatRoute) {
            console.log('🔍 OAuthWrapper: Other OAuth indicators found, checking backend...');
            
            try {
              // Check if backend has a session for this user
              const response = await axiosInstance.get('/api/v1/auth/user');
              
              if (response.status === 200) {
                console.log('⚠️ OAuthWrapper: Backend session exists but no sessionId in URL');
                console.log('This suggests the OAuth handler might not be passing the session ID correctly');
                
                // Try to get session from headers if available
                const sessionFromHeaders = response.headers['x-auth-token'];
                if (sessionFromHeaders) {
                  console.log('✅ OAuthWrapper: Found session in response headers:', sessionFromHeaders);
                  await authSessionStorageService.setSession(sessionFromHeaders);
                  await authSessionStorageService.setLoginTime();
                }
                
                // Also get CSRF token
                const csrfToken = response.headers['x-csrf-token'];
                if (csrfToken) {
                  await authSessionStorageService.setCsrfToken(csrfToken);
                  console.log('✅ OAuthWrapper: CSRF token updated from fallback response');
                }
                
                // CRITICAL: Also refresh auth context in this case
                console.log('🔄 OAuthWrapper: Triggering fallback auth refresh...');
                await refreshAuth();
                console.log('✅ OAuthWrapper: Fallback auth context refreshed');
                
                localStorage.setItem('oauth_session_active', 'true');
                localStorage.setItem('oauth_user_data', JSON.stringify(response.data));
              }
            } catch (error: any) {
              console.log('❌ OAuthWrapper: No backend session found:', error.response?.status || error.message);
            }
          } else {
            console.log('🔍 OAuthWrapper: No OAuth callback detected');
          }
        }
      } catch (error) {
        console.error('❌ OAuthWrapper: Unexpected error during OAuth check:', error);
      } finally {
        setIsCheckingOAuth(false);
      }
    };

    // Run the check after a small delay to ensure page is loaded
    const timer = setTimeout(checkOAuthCallback, 300);
    
    return () => clearTimeout(timer);
  }, [refreshAuth]);

  // Show loading while checking OAuth
  if (isCheckingOAuth) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <Loader2 size={48} className="text-blue-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Completing Sign In
          </h2>
          <p className="text-gray-600">
            Please wait while we verify your authentication...
          </p>
        </div>
      </div>
    );
  }

  // OAuth check complete, render children
  return <>{children}</>;
};